import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Alert,
  LinearProgress,
  Card,
  CardContent,
  Grid,
  Chip,
} from "@mui/material";
import { useAICalendar } from "contexts/AICalendarContext";
import _request from "actions/_request";
import i18n from "i18";

/**
 * Step 3: Optimize Schedule
 * Review optimized posting schedule
 * @returns {JSX.Element} Step 3 Schedule display
 */
export default function Step3Schedule() {
  const { state, nextStep, prevStep, setSchedule } = useAICalendar();
  
  const [loading, setLoading] = useState(false);
  const [localSchedule, setLocalSchedule] = useState([]);
  const [summary, setSummary] = useState(null);
  const [localError, setLocalError] = useState(null);

  // Load schedule on mount if not already loaded
  useEffect(() => {
    if (state.schedule.length > 0) {
      setLocalSchedule(state.schedule);
    } else {
      loadSchedule();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSchedule = async () => {
    setLoading(true);
    setLocalError(null);

    try {
      const params = {
        topics: state.topics,
        time_period: state.timeRange,
        account_ids: state.selectedAccounts,
        user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      const response = await _request.aiCalendar.optimizeSchedule(params);

      if (response?.isOk && response?.data) {
        setLocalSchedule(response.data.schedule || []);
        setSummary(response.data.summary);
        setSchedule(response.data.schedule || []);
      } else {
        setLocalError(response?.error || i18n.t("aiCalendarWizard.errors.scheduleFailed"));
      }
    } catch (error) {
      console.error("Schedule optimization failed:", error);
      setLocalError(error.message || i18n.t("aiCalendarWizard.errors.generic"));
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (localSchedule.length === 0) {
      setLocalError(i18n.t("aiCalendarWizard.errors.noSchedule"));
      return;
    }
    setSchedule(localSchedule);
    nextStep();
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Typography variant="h6">
        {i18n.t("aiCalendarWizard.step3.title")}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {i18n.t("aiCalendarWizard.step3.description")}
      </Typography>

      {loading && (
        <Box>
          <LinearProgress />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            {i18n.t("aiCalendarWizard.analyzing")}
          </Typography>
        </Box>
      )}

      {localError && (
        <Alert severity="error" onClose={() => setLocalError(null)}>
          {localError}
        </Alert>
      )}

      {/* Summary */}
      {summary && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
              {i18n.t("aiCalendarWizard.step3.summary")}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  {i18n.t("aiCalendarWizard.step3.totalSlots")}
                </Typography>
                <Typography variant="h6">{summary.total_slots}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  {i18n.t("aiCalendarWizard.step3.postsPerWeek")}
                </Typography>
                <Typography variant="h6">{summary.posts_per_week_avg}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  {i18n.t("aiCalendarWizard.step3.weekdays")}
                </Typography>
                <Typography variant="h6">{summary.coverage?.weekdays || 0}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  {i18n.t("aiCalendarWizard.step3.weekends")}
                </Typography>
                <Typography variant="h6">{summary.coverage?.weekends || 0}</Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Schedule Calendar */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {i18n.t("aiCalendarWizard.step3.schedule")}
        </Typography>
        <Grid container spacing={2}>
          {localSchedule.map((day, dayIndex) => (
            <Grid item xs={12} key={dayIndex}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                    {new Date(day.date).toLocaleDateString(i18n.language, {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {day.slots.map((slot, slotIndex) => (
                      <Box
                        key={slotIndex}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          p: 1,
                          borderRadius: 1,
                          bgcolor: "action.hover",
                        }}
                      >
                        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                          <Typography variant="body2" fontWeight="bold">
                            {slot.time}
                          </Typography>
                          <Typography variant="body2">
                            {slot.topic_title}
                          </Typography>
                        </Box>
                        <Chip
                          label={slot.suggested_platform}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Actions */}
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mt: 2 }}>
        <Button variant="outlined" onClick={prevStep}>
          {i18n.t("aiCalendarWizard.back")}
        </Button>
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={localSchedule.length === 0}
        >
          {i18n.t("aiCalendarWizard.next")}
        </Button>
      </Box>
    </Box>
  );
}
